import type { ResourceType } from '@/services/api/api.types'
import { getYouTubeVideoId } from '@/utils/urlValidation'

const THUMBNAIL_SUPPORTED_TYPES: ReadonlySet<ResourceType> = new Set<ResourceType>([
  'image',
  'video',
  'audio',
  'pdf',
  'pptx',
  'docx',
  'youtube',
])

type TypedResource = { type?: ResourceType | null }

/** Whether a step's source file can carry a thumbnail — one rule for the editor and View Module. */
export function supportsThumbnail(source: TypedResource | undefined, uploaded: TypedResource | undefined): boolean {
  const type = uploaded?.type ?? source?.type
  return !!source && !!type && THUMBNAIL_SUPPORTED_TYPES.has(type)
}

/** Subset of a `GetResourcesResponse` entry needed to resolve a thumbnail. */
export type ThumbnailSource = {
  type?: ResourceType | null
  thumbnailS3Key?: string | null
  youtubeId?: string | null
  data?: string | null
  sourceUrl?: string | null
}

/**
 * YouTube resources never get a `thumbnailS3Key` — the backend stores the watch
 * URL and never transcodes, so there is no frame to capture. YouTube's CDN
 * serves a still per video id, which is what the app renders too.
 */
export function getYouTubeThumbnailUrl(source: ThumbnailSource): string | null {
  if (source.type !== 'youtube') return null

  const videoId = source.youtubeId || getYouTubeVideoId(source.data ?? '') || getYouTubeVideoId(source.sourceUrl ?? '')

  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null
}

/**
 * Display URL for a resource's thumbnail. A stored thumbnail always wins over
 * the derived YouTube still so manual uploads keep taking effect.
 */
export function resolveThumbnailUrl(source: ThumbnailSource, serverBaseUrl: string | undefined): string | null {
  if (source.thumbnailS3Key) {
    return serverBaseUrl ? `${serverBaseUrl}/files/${source.thumbnailS3Key}` : null
  }
  return getYouTubeThumbnailUrl(source)
}
